import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * Where each step of this tab's history went, by the router's own index. Back
 * on a phone means "the screen I came from" when that screen is the parent, and
 * "the parent" when the page was opened from a link or a refresh.
 */
const visited = new Map<number, string>();

function currentIndex(): number {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : 0;
}

/** Called once by the shell: remembers the path at each history index. */
export function useTrackHistory(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    visited.set(currentIndex(), pathname);
  }, [pathname]);
}

/** True when there is a screen of this app to go back to. */
export function canGoBack(): boolean {
  return currentIndex() > 0 && visited.has(currentIndex() - 1);
}

/**
 * Back to `parent`: steps back through history when that is where the person
 * came from, so the list keeps its place, else opens the parent in this step.
 */
export function useGoBack(): (parent: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (parent: string) => {
      const prev = visited.get(currentIndex() - 1);
      if (prev !== undefined && samePlace(prev, parent)) navigate(-1);
      else navigate(parent, { replace: true });
    },
    [navigate],
  );
}

function samePlace(a: string, b: string): boolean {
  const clean = (p: string) => p.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  return clean(a) === clean(b);
}

/**
 * The window React Router listens on, hearing the browser's back and forward a moment later. Why: React
 * finishes an update made while the browser handles back at once, for the browser's own scroll restoring,
 * and an update finished at once never slides; heard a moment later, back slides as forward does. The app
 * restores its own scroll (useScrollMemory), so nothing is lost.
 */
export function windowForRouter(): Window {
  const later = new Map<EventListenerOrEventListenerObject, EventListener>();
  return new Proxy(window, {
    get(target, prop) {
      if (prop === "addEventListener" || prop === "removeEventListener") {
        return (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) => {
          if (type !== "popstate") return target[prop](type, listener, options);
          if (prop === "addEventListener") {
            const heard: EventListener = (e) => {
              window.setTimeout(() => (typeof listener === "function" ? listener(e) : listener.handleEvent(e)));
            };
            later.set(listener, heard);
            target.addEventListener(type, heard, options);
          } else {
            const heard = later.get(listener);
            if (heard) target.removeEventListener(type, heard, options);
            later.delete(listener);
          }
        };
      }
      const value: unknown = Reflect.get(target, prop, target);
      return typeof value === "function" ? (value as (...a: unknown[]) => unknown).bind(target) : value;
    },
  });
}
